/**
 * Program policies (docs/14-program-policies.md). Each key is set, or left unset, per layer:
 * organization → program → branch → circle (most specific wins; a general layer may lock a key).
 * This file defines the keys and the shape of their values only; resolution lives in @mubin/domain and
 * every write is validated against these schemas on the server.
 */
import { z } from "zod";
import { ProgramKind } from "./admin.js";
export const POLICY_SCOPES = ["organization", "program", "branch", "circle"];
export const PolicyScope = z.enum(POLICY_SCOPES);
/** approved: adopted by the organization. provisional: a working value, shown as not approved. */
export const PolicyStatus = z.enum(["provisional", "approved"]);
export const WEEKDAYS = ["sat", "sun", "mon", "tue", "wed", "thu", "fri"];
export const Weekday = z.enum(WEEKDAYS);
/**
 * Units the plan engine can measure in (B2). "page" is the وجه of the Madinah mushaf. Lines (سطر) need
 * line-level mushaf data that packages/quran does not ship yet (docs/open-questions.md).
 */
export const AmountUnit = z.enum(["ayah", "page", "rub", "juz", "surah"]);
export const Amount = z.object({
    amount: z.number().positive().max(604),
    unit: AmountUnit,
});
const positive = z.number().positive().max(1000);
export const POLICY_GROUPS = [
    "memorization",
    "review",
    "taahud",
    "grading",
    "absence",
    "struggling",
    "results",
];
const key = (def) => def;
export const POLICY_KEYS = {
    // B2, B3, B4
    "memorization.unit": key({ schema: AmountUnit, group: "memorization" }),
    "memorization.direction": key({ schema: z.enum(["backward", "forward"]), group: "memorization" }),
    "memorization.dailyAmount": key({ schema: positive, group: "memorization" }),
    "memorization.minDailyAmount": key({ schema: positive, group: "memorization" }),
    "memorization.maxDailyAmount": key({ schema: positive, group: "memorization" }),
    "schedule.studyDays": key({
        schema: z
            .array(Weekday)
            .min(1)
            .max(7)
            .refine((d) => new Set(d).size === d.length, "يوم مكرر"),
        group: "memorization",
    }),
    // B5
    "review.near": key({ schema: Amount, group: "review" }),
    "review.far": key({ schema: Amount, group: "review" }),
    // B4, B5: taahud only
    "taahud.streakIncrease": key({
        schema: z.object({
            enabled: z.boolean(),
            days: z.number().int().min(1).max(60),
            increment: positive,
        }),
        group: "taahud",
        programKinds: ["taahud"],
    }),
    "taahud.weeklyReview": key({
        schema: z.object({ enabled: z.boolean(), day: Weekday }),
        group: "taahud",
        programKinds: ["taahud"],
    }),
    // B7, B8
    "grading.maxScore": key({ schema: z.number().int().min(1).max(1000), group: "grading" }),
    "grading.passScore": key({ schema: z.number().min(0).max(1000), group: "grading" }),
    "attempts.max": key({ schema: z.number().int().min(1).max(20), group: "grading" }),
    "attempts.onExhausted": key({
        schema: z.enum(["repeat_amount", "refer_supervisor"]),
        group: "grading",
    }),
    // B9
    "absence.policy": key({ schema: z.enum(["shift", "carry_over", "skip", "pause"]), group: "absence" }),
    "absence.carryOverAlert": key({ schema: positive, group: "absence" }),
    // B10: indicators of a struggling student over a period; an indicator counts when its threshold is set
    // and reached. One indicator is not enough unless the program says so (minIndicators = 1).
    "struggling.rules": key({
        schema: z
            .object({
            periodDays: z.number().int().min(1).max(120),
            minIndicators: z.number().int().min(1).max(4),
            /** Results decided «إعادة» (تكرار الرسوب). */
            repeats: z.number().int().min(1).max(100).nullable(),
            /** New portions past their day and not passed (عدم إنجاز المقدار). */
            unfinished: z.number().int().min(1).max(100).nullable(),
            /** Days recorded with a state that does not count as present (تكرار الغياب). */
            absences: z.number().int().min(1).max(100).nullable(),
            /** Largest amount carried over, in the plan's unit (تراكم غير المنجز). */
            carried: positive.nullable(),
        })
            .refine((r) => [r.repeats, r.unfinished, r.absences, r.carried].filter((v) => v !== null).length >= r.minIndicators, "عدد المؤشرات المحددة أقل من الحد الأدنى المطلوب"),
        group: "struggling",
    }),
    // B13
    "results.editWindowHours": key({
        schema: z
            .number()
            .int()
            .min(0)
            .max(24 * 30),
        group: "results",
    }),
};
export const POLICY_KEY_NAMES = Object.keys(POLICY_KEYS);
export const PolicyKeyName = z.enum(POLICY_KEY_NAMES);
/** Validates a value for a key; throws a ZodError when it does not fit. */
export function parsePolicyValue(k, value) {
    return POLICY_KEYS[k].schema.parse(value);
}
// ---------- API ----------
export const PolicyQuery = z.object({
    organizationId: z.uuid(),
    scopeType: PolicyScope,
    scopeId: z.uuid().optional(),
});
export const SetPolicyBody = z.object({
    organizationId: z.uuid(),
    scopeType: PolicyScope,
    scopeId: z.uuid().optional(),
    key: PolicyKeyName,
    value: z.unknown(),
    status: PolicyStatus.default("provisional"),
    locked: z.boolean().default(false),
});
export const ClearPolicyBody = SetPolicyBody.pick({
    organizationId: true,
    scopeType: true,
    scopeId: true,
    key: true,
});
export const PolicyLayerRef = z.object({
    scopeType: PolicyScope,
    scopeId: z.uuid(),
    name: z.string(),
});
export const PolicySettingView = z.object({
    value: z.unknown(),
    status: PolicyStatus,
    locked: z.boolean(),
    updatedAt: z.string(),
    approvedAt: z.string().nullable(),
});
export const PolicyEntry = z.object({
    key: PolicyKeyName,
    /** What this layer itself sets (null: inherits). */
    own: PolicySettingView.nullable(),
    /** The value that applies here, and the layer it comes from (null: unset everywhere). */
    effective: PolicySettingView.extend({ source: PolicyLayerRef }).nullable(),
    /** A more general layer locked this key; this layer cannot set it. */
    lockedBy: PolicyLayerRef.nullable(),
    /** False when the key does not apply here (taahud keys outside taahud programs). */
    applicable: z.boolean(),
});
export const PoliciesResponse = z.object({
    /** The layers consulted, most general first, ending with the requested one. */
    layers: z.array(PolicyLayerRef),
    entries: z.array(PolicyEntry),
    /** May the caller change this layer, and approve values? */
    canEdit: z.boolean(),
    canApprove: z.boolean(),
});
//# sourceMappingURL=policies.js.map